import {
    BudgetBys,
    BudgetExpenseOrIncome,
    BudgetSections,
} from 'business/constants';
import { UseQueryResult } from '@tanstack/react-query';
import { HttpError, RQAPICallWithData } from 'store/APIClient';
import {
    BudgetLineQueryKey,
    createBudgetQueryKeys,
} from '../createBudgetQueryKeys';
import { useBudgetLinesQuery } from './useCustomQuery';
import { snakify } from 'helpers/syntaxHelper';
import { BudgetLine } from 'routes/BudgetModule/config/types';

type BudgetLinesQueryParams = {
    budgetBy: BudgetBys;
    expenseOrIncome?: BudgetExpenseOrIncome;
    investmentOperating?: BudgetSections | null;
    exercice?: number;
    operationSoftwareId?: string;
    budgetName?: string;
    resourceSoftwareId?: string;
    enabled?: boolean;
};

type BudgetLinesQueryResultWithCacheKey = UseQueryResult<
    BudgetLine[],
    HttpError
> & {
    cacheKey: BudgetLineQueryKey;
};

/**
 * A custom hook to fetch budget lines data based on the provided filters and parameters.
 *
 * @param budgetBy - Specifies how the budget is organized (e.g., by department, by resource).
 * @param expenseOrIncome - Filter by expense or income type (optional).
 * @param investmentOperating - Filter by section : operating or investment (optional).
 * @param exercice - Specifies the exercise (optional).
 * @param operationSoftwareId - Operation software identifier (optional).
 * @param budgetName - Budget name (optional).
 * @param resourceSoftwareId - Resource software identifier (optional).
 *
 * @returns The fetched budget lines data and the cache key.
 */
const useGetBudgetLines = ({
    budgetBy,
    expenseOrIncome,
    investmentOperating,
    exercice,
    operationSoftwareId,
    budgetName,
    resourceSoftwareId,
    enabled = true,
}: BudgetLinesQueryParams): BudgetLinesQueryResultWithCacheKey => {
    const { budgetLinesKeys } = createBudgetQueryKeys();

    const cacheKey = budgetLinesKeys({
        expenseOrIncome,
        investmentOperating,
        exercice,
        operationSoftwareId,
        budgetName,
        resourceSoftwareId,
    });

    const query: UseQueryResult<BudgetLine[], HttpError> = useBudgetLinesQuery(
        cacheKey,
        async () => {
            const response = await RQAPICallWithData<BudgetLine[]>({
                endpoint: 'budget_lines',
                method: 'POST',
                body: {
                    expenseIncome: expenseOrIncome,
                    investmentOperating: investmentOperating,
                    budgetBy: snakify(budgetBy),
                    operationSoftwareId,
                    exercice,
                    budgetName,
                    resourceSoftwareId,
                },
            });

            return response.content.data;
        },
        { enabled },
    );
    return {
        ...query,
        cacheKey,
    };
};

/**
 * Fetches budget line for the move inscription modal.
 */
export const useGetMoveBudgetLines = (
    params: Pick<
        BudgetLinesQueryParams,
        | 'budgetBy'
        | 'investmentOperating'
        | 'expenseOrIncome'
        | 'exercice'
        | 'budgetName'
        | 'resourceSoftwareId'
        | 'enabled'
    >,
): BudgetLinesQueryResultWithCacheKey => useGetBudgetLines(params);

/**
 * Fetches budget line for the add inscription modal.
 */
export const useGetAddBudgetLines = (
    params: Pick<
        BudgetLinesQueryParams,
        | 'budgetBy'
        | 'investmentOperating'
        | 'expenseOrIncome'
        | 'exercice'
        | 'budgetName'
        | 'resourceSoftwareId'
        | 'operationSoftwareId'
        | 'enabled'
    >,
): BudgetLinesQueryResultWithCacheKey => useGetBudgetLines(params);
